/**
 * A QR encoder, in about two hundred lines, with no dependency.
 *
 * ── WHY THIS IS HERE AND NOT A LIBRARY ───────────────────────────────────
 * The join code, the link and the QR are the whole product on day one, and the
 * poster ends up on an advising office door, which is the highest-conversion
 * surface P3 will ever have at a university. A decorative block that looks
 * like a QR and does not scan is worse than no QR at all, so this encodes for
 * real: byte mode, error correction level M, versions 1 to 6, mask chosen by
 * the specified penalty rules.
 *
 * Level M recovers about 15% of a damaged symbol, which is the right level for
 * something printed and taped to a wall.
 *
 * Version 6 at level M holds 106 bytes. Every join URL P3 can issue is under
 * forty. Versions 7 and up are not implemented because they need the version
 * information blocks, and nothing on this surface will ever reach one.
 *
 * ── HOW IT WAS VERIFIED, BECAUSE "IT LOOKS LIKE A QR" IS NOT VERIFICATION ─
 *   1. Module-for-module against a reference encoder (segno 1.6.6) at every
 *      one of the eight masks, for payloads that fill a version exactly. The
 *      matrices are identical. Where a payload does not fill its version,
 *      segno emits one extra zero pad byte before the 0xEC / 0x11 sequence;
 *      that lies after the terminator, so both symbols carry the same message
 *      and both decode.
 *   2. Decoded back to the original string by OpenCV's detector, for the join
 *      URL and for a spread of lengths covering all six versions and every
 *      padding case. Twenty-seven symbols, twenty-seven exact round trips.
 *
 * The bug that verification caught, kept here because it is invisible by eye:
 * the vertical timing column is skipped by moving the placement loop variable
 * itself. Skipping it with a local copy re-walks two columns and never reaches
 * column zero. The symbol still looks like a QR code. It does not decode.
 */

import { useMemo } from "react";
import type { Ref } from "react";

/* ==========================================================================
   GF(256), the field the Reed-Solomon codewords live in.
   Primitive polynomial x^8 + x^4 + x^3 + x^2 + 1, which is 0x11D.
   ========================================================================== */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255] ?? 0;
}

function gmul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[(LOG[a] ?? 0) + (LOG[b] ?? 0)] ?? 0;
}

/** The generator polynomial for `degree` error-correction codewords, as
 *  coefficients from the highest power down. */
function rsGenerator(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      const coefficient = poly[j] ?? 0;
      next[j] = (next[j] ?? 0) ^ coefficient;
      next[j + 1] = (next[j + 1] ?? 0) ^ gmul(coefficient, EXP[i] ?? 0);
    }
    poly = next;
  }
  return poly;
}

/** The remainder of data * x^ecLength divided by the generator. */
function rsRemainder(data: readonly number[], ecLength: number): number[] {
  const generator = rsGenerator(ecLength);
  const result = new Array<number>(ecLength).fill(0);
  for (const byte of data) {
    const factor = byte ^ (result[0] ?? 0);
    result.shift();
    result.push(0);
    for (let i = 0; i < ecLength; i++) {
      result[i] = (result[i] ?? 0) ^ gmul(generator[i + 1] ?? 0, factor);
    }
  }
  return result;
}

/* ==========================================================================
   Version tables, level M only.
   [data codewords, EC codewords per block, blocks, alignment centre]
   ========================================================================== */

interface VersionSpec {
  readonly dataCodewords: number;
  readonly ecPerBlock: number;
  readonly blocks: number;
  /** The second alignment-pattern centre. Version 1 has no alignment pattern. */
  readonly alignment: number | null;
}

const VERSIONS: readonly VersionSpec[] = [
  { dataCodewords: 16, ecPerBlock: 10, blocks: 1, alignment: null },
  { dataCodewords: 28, ecPerBlock: 16, blocks: 1, alignment: 18 },
  { dataCodewords: 44, ecPerBlock: 26, blocks: 1, alignment: 22 },
  { dataCodewords: 64, ecPerBlock: 18, blocks: 2, alignment: 26 },
  { dataCodewords: 86, ecPerBlock: 24, blocks: 2, alignment: 30 },
  { dataCodewords: 108, ecPerBlock: 16, blocks: 4, alignment: 34 },
];

/* ==========================================================================
   The matrix
   ========================================================================== */

export interface QrMatrix {
  readonly size: number;
  readonly modules: readonly (readonly boolean[])[];
  readonly version: number;
}

class Grid {
  readonly size: number;
  readonly modules: boolean[][];
  readonly reserved: boolean[][];

  constructor(size: number) {
    this.size = size;
    this.modules = Array.from({ length: size }, () =>
      new Array<boolean>(size).fill(false),
    );
    this.reserved = Array.from({ length: size }, () =>
      new Array<boolean>(size).fill(false),
    );
  }

  /** Column, row: the order the specification's diagrams use. */
  setFunction(col: number, row: number, dark: boolean): void {
    if (col < 0 || row < 0 || col >= this.size || row >= this.size) return;
    const line = this.modules[row];
    const mask = this.reserved[row];
    if (!line || !mask) return;
    line[col] = dark;
    mask[col] = true;
  }

  at(col: number, row: number): boolean {
    return this.modules[row]?.[col] ?? false;
  }

  isReserved(col: number, row: number): boolean {
    return this.reserved[row]?.[col] ?? false;
  }
}

function drawFinder(grid: Grid, centreCol: number, centreRow: number): void {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      grid.setFunction(centreCol + dx, centreRow + dy, distance !== 2 && distance !== 4);
    }
  }
}

function drawAlignment(grid: Grid, centreCol: number, centreRow: number): void {
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      grid.setFunction(
        centreCol + dx,
        centreRow + dy,
        Math.max(Math.abs(dx), Math.abs(dy)) !== 1,
      );
    }
  }
}

/** BCH(15,5) over the five format bits, then the specification's XOR mask. */
function formatBits(mask: number): number {
  // Level M is 0b00, so the five-bit datum is just the mask index.
  const data = mask;
  let remainder = data;
  for (let i = 0; i < 10; i++) {
    remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
  }
  return ((data << 10) | remainder) ^ 0x5412;
}

function drawFormat(grid: Grid, mask: number): void {
  const bits = formatBits(mask);
  const bit = (i: number) => ((bits >>> i) & 1) !== 0;
  const size = grid.size;

  for (let i = 0; i <= 5; i++) grid.setFunction(8, i, bit(i));
  grid.setFunction(8, 7, bit(6));
  grid.setFunction(8, 8, bit(7));
  grid.setFunction(7, 8, bit(8));
  for (let i = 9; i < 15; i++) grid.setFunction(14 - i, 8, bit(i));

  for (let i = 0; i < 8; i++) grid.setFunction(size - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) grid.setFunction(8, size - 15 + i, bit(i));

  // The one module that is always dark.
  grid.setFunction(8, size - 8, true);
}

function maskAt(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
  }
}

/* The four penalty rules, straight from the specification. A poor mask does
   not make a symbol invalid, it makes it harder to scan on a printed page at
   an angle, which is exactly the condition a poster on a door is read in. */
const FINDER_LIKE = [true, false, true, true, true, false, true];

function penalty(grid: Grid): number {
  const size = grid.size;
  let score = 0;

  const lines: boolean[][] = [];
  for (let row = 0; row < size; row++) {
    const line: boolean[] = [];
    for (let col = 0; col < size; col++) line.push(grid.at(col, row));
    lines.push(line);
  }
  for (let col = 0; col < size; col++) {
    const line: boolean[] = [];
    for (let row = 0; row < size; row++) line.push(grid.at(col, row));
    lines.push(line);
  }

  // Rule 1: runs of five or more of one colour.
  for (const line of lines) {
    let run = 1;
    for (let i = 1; i < line.length; i++) {
      if (line[i] === line[i - 1]) {
        run++;
      } else {
        if (run >= 5) score += 3 + (run - 5);
        run = 1;
      }
    }
    if (run >= 5) score += 3 + (run - 5);
  }

  // Rule 3: a finder-like run with four light modules on either side.
  for (const line of lines) {
    for (let i = 0; i + 6 < line.length; i++) {
      let hit = true;
      for (let k = 0; k < 7; k++) {
        if (line[i + k] !== FINDER_LIKE[k]) {
          hit = false;
          break;
        }
      }
      if (!hit) continue;
      const before = line.slice(Math.max(0, i - 4), i);
      const after = line.slice(i + 7, i + 11);
      if (before.length === 4 && before.every((v) => !v)) score += 40;
      if (after.length === 4 && after.every((v) => !v)) score += 40;
    }
  }

  // Rule 2: blocks of the same colour, two by two.
  for (let row = 0; row + 1 < size; row++) {
    for (let col = 0; col + 1 < size; col++) {
      const a = grid.at(col, row);
      if (
        a === grid.at(col + 1, row) &&
        a === grid.at(col, row + 1) &&
        a === grid.at(col + 1, row + 1)
      ) {
        score += 3;
      }
    }
  }

  // Rule 4: the balance of dark to light.
  let dark = 0;
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) if (grid.at(col, row)) dark++;
  }
  const total = size * size;
  const percent = (dark * 100) / total;
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/**
 * Encode `text` as a QR symbol. Returns the module matrix without a quiet
 * zone; the renderer adds it.
 */
export function qrMatrix(text: string): QrMatrix {
  const bytes = [...new TextEncoder().encode(text)];

  let versionIndex = -1;
  for (let i = 0; i < VERSIONS.length; i++) {
    const spec = VERSIONS[i];
    if (!spec) continue;
    // Four bits of mode, eight of length, then the payload.
    if (spec.dataCodewords * 8 >= 4 + 8 + bytes.length * 8) {
      versionIndex = i;
      break;
    }
  }
  if (versionIndex < 0) {
    throw new Error("QR payload is too long for versions 1 to 6 at level M");
  }
  const spec = VERSIONS[versionIndex];
  if (!spec) throw new Error("unreachable: version spec missing");
  const version = versionIndex + 1;
  const size = 17 + 4 * version;

  /* ---- Bitstream ---- */
  const bits: number[] = [];
  const push = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  push(0b0100, 4);
  push(bytes.length, 8);
  for (const byte of bytes) push(byte, 8);

  const capacity = spec.dataCodewords * 8;
  push(0, Math.min(4, capacity - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let k = 0; k < 8; k++) byte = (byte << 1) | (bits[i + k] ?? 0);
    data.push(byte);
  }
  const PADDING = [0xec, 0x11];
  for (let i = 0; data.length < spec.dataCodewords; i++) {
    data.push(PADDING[i % 2] ?? 0xec);
  }

  /* ---- Blocks, then interleave. Every implemented version has equal-sized
         blocks, which is why there is no short-block branch here. ---- */
  const perBlock = spec.dataCodewords / spec.blocks;
  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  for (let b = 0; b < spec.blocks; b++) {
    const block = data.slice(b * perBlock, (b + 1) * perBlock);
    dataBlocks.push(block);
    ecBlocks.push(rsRemainder(block, spec.ecPerBlock));
  }

  const codewords: number[] = [];
  for (let i = 0; i < perBlock; i++) {
    for (const block of dataBlocks) codewords.push(block[i] ?? 0);
  }
  for (let i = 0; i < spec.ecPerBlock; i++) {
    for (const block of ecBlocks) codewords.push(block[i] ?? 0);
  }

  /* ---- Function patterns ---- */
  const grid = new Grid(size);
  for (let i = 0; i < size; i++) {
    grid.setFunction(6, i, i % 2 === 0);
    grid.setFunction(i, 6, i % 2 === 0);
  }
  drawFinder(grid, 3, 3);
  drawFinder(grid, size - 4, 3);
  drawFinder(grid, 3, size - 4);
  if (spec.alignment !== null) {
    const centres = [6, spec.alignment];
    for (const row of centres) {
      for (const col of centres) {
        const isFinderCorner =
          (row === 6 && col === 6) ||
          (row === 6 && col === spec.alignment) ||
          (row === spec.alignment && col === 6);
        if (isFinderCorner) continue;
        drawAlignment(grid, col, row);
      }
    }
  }
  drawFormat(grid, 0);

  /* ---- Codewords, upward then downward in two-column strips ---- */
  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    // The vertical timing column is skipped, and the loop variable itself has
    // to move: leaving it at 6 and using a local copy makes the next strip
    // start from 4 instead of 3, which re-walks two columns and never reaches
    // column 0. The symbol still looks like a QR and does not decode.
    if (right === 6) right = 5;
    for (let vertical = 0; vertical < size; vertical++) {
      for (let j = 0; j < 2; j++) {
        const col = right - j;
        const row = upward ? size - 1 - vertical : vertical;
        if (grid.isReserved(col, row)) continue;
        const byte = codewords[bitIndex >>> 3];
        const dark =
          byte !== undefined && ((byte >>> (7 - (bitIndex & 7))) & 1) !== 0;
        const line = grid.modules[row];
        if (line) line[col] = dark;
        bitIndex++;
      }
    }
    upward = !upward;
  }

  /* ---- Mask selection ---- */
  let bestMask = 0;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(grid, mask);
    drawFormat(grid, mask);
    const score = penalty(grid);
    if (score < bestScore) {
      bestScore = score;
      bestMask = mask;
    }
    applyMask(grid, mask);
  }
  applyMask(grid, bestMask);
  drawFormat(grid, bestMask);

  return { size, modules: grid.modules, version };
}

function applyMask(grid: Grid, mask: number): void {
  for (let row = 0; row < grid.size; row++) {
    const line = grid.modules[row];
    if (!line) continue;
    for (let col = 0; col < grid.size; col++) {
      if (grid.isReserved(col, row)) continue;
      if (maskAt(mask, row, col)) line[col] = !(line[col] ?? false);
    }
  }
}

/* ==========================================================================
   The component
   ========================================================================== */

const QUIET = 4;

/**
 * Rendered as merged horizontal runs rather than one rect per module, which
 * takes a version 3 symbol from about 900 nodes to about 200 and keeps a
 * printed PDF small.
 */
export function QrCode({
  text,
  label,
  className,
  ref,
}: {
  text: string;
  /** The accessible name. A QR image with no name is a dead end for anyone
   *  who cannot point a camera at the screen, so the name says what it
   *  encodes and the code is also printed beside it as text. */
  label: string;
  className?: string;
  /** Handed to the SVG itself, so the download serializes exactly the element
   *  on screen rather than re-running the encoder down a second path. */
  ref?: Ref<SVGSVGElement>;
}) {
  const symbol = useMemo(() => {
    try {
      return qrMatrix(text);
    } catch {
      return null;
    }
  }, [text]);

  if (!symbol) return null;

  const span = symbol.size + QUIET * 2;
  const runs: { x: number; y: number; w: number }[] = [];
  for (let row = 0; row < symbol.size; row++) {
    const line = symbol.modules[row];
    if (!line) continue;
    let start = -1;
    for (let col = 0; col <= symbol.size; col++) {
      const dark = col < symbol.size && (line[col] ?? false);
      if (dark && start < 0) start = col;
      if (!dark && start >= 0) {
        runs.push({ x: start + QUIET, y: row + QUIET, w: col - start });
        start = -1;
      }
    }
  }

  return (
    <svg
      ref={ref}
      className={className}
      viewBox={`0 0 ${span} ${span}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={label}
    >
      <rect width={span} height={span} fill="#FFFFFF" />
      {runs.map((run) => (
        <rect
          key={`${run.y}-${run.x}`}
          x={run.x}
          y={run.y}
          width={run.w}
          height={1}
          fill="#1A1A1A"
        />
      ))}
    </svg>
  );
}
